// components/chat/CallHistoryPanel.jsx
import React, { useState, useEffect, useContext } from "react";
import { Video, Phone, ArrowUpRight, ArrowDownLeft, PhoneMissed } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";

const NEON     = "#00FF99";
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const fmt = secs => {
  if (!secs || secs === "0") return "—";
  const s = parseInt(secs);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s/60)}m ${s%60}s`;
};

const niceTime = iso => {
  if (!iso) return "";
  const d   = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 60000);
  if (diff < 1)    return "just now";
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
  if (diff < 10080) return d.toLocaleDateString([], { weekday:"short", hour:"2-digit", minute:"2-digit" });
  return d.toLocaleDateString([], { day:"2-digit", month:"short", year:"numeric" });
};

export default function CallHistoryPanel({ type, onClose, onCall }) {
  const { user }               = useContext(AuthContext);
  const [logs,    setLogs]     = useState([]);
  const [loading, setLoading]  = useState(true);

  const isVideo = type === "video";

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch(`${API_BASE}/api/calling/logs?call_type=${type}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(r => r.ok ? r.json() : { logs:[] })
      .then(d => setLogs(d.logs || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [user, type]);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#0B0B0B", fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:"1px solid rgba(0,255,153,0.15)", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div>
            <p style={{ color:"#fff", fontWeight:700, fontSize:15 }}>{isVideo ? "Video" : "Audio"} Call History</p>
            <p style={{ color:"rgba(255,255,255,0.3)", fontSize:11 }}>{logs.length} call{logs.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <button onClick={onClose} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", fontSize:18, padding:"4px 8px", borderRadius:8 }}>✕</button>
      </div>

      {/* List */}
      <div style={{ flex:1, overflowY:"auto", padding:"12px 16px" }}>
        {loading && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:120 }}>
            <div style={{ width:28, height:28, borderRadius:"50%", border:`2px solid ${NEON}`, borderTopColor:"transparent", animation:"spin 0.8s linear infinite" }} />
          </div>
        )}

        {!loading && logs.length === 0 && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:200, gap:12 }}>
            {isVideo
              ? <Video size={40} strokeWidth={1.8} color="rgba(255,255,255,0.25)" />
              : <Phone size={40} strokeWidth={1.8} color="rgba(255,255,255,0.25)" />}
            <p style={{ color:"rgba(255,255,255,0.2)", fontSize:13, textAlign:"center" }}>
              No {isVideo ? "video" : "audio"} call history yet
            </p>
          </div>
        )}

        <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
          {logs.map(log => {
            const isOut  = log.direction === "outgoing";
            const missed = log.status === "missed";

            return (
              <div key={log.id} style={{
                display:"flex", alignItems:"center", gap:12,
                padding:"10px 12px", borderRadius:12,
                background:"rgba(255,255,255,0.03)",
                border:"1px solid rgba(255,255,255,0.06)",
                transition:"background 0.15s", cursor:"pointer",
              }}
              onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.06)"}
              onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,0.03)"}
              >
                {/* Avatar */}
                <div style={{
                  width:36, height:36, borderRadius:"50%", flexShrink:0,
                  background:"#0d0d0d",
                  border:"1.5px solid rgba(0,255,153,0.4)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:13, fontWeight:600, color:"#00FF99",
                  textTransform:"uppercase",
                }}>
                  {(() => {
                    const p = (log.other_name || "?").trim().split(/\s+/).filter(Boolean);
                    return ((p[0]?.[0] || "?") + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
                  })()}
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                    {/* Direction icon */}
                    <span style={{
                      display:"inline-flex", alignItems:"center", gap:4,
                      fontSize:11,
                      color: missed ? "#f87171" : isOut ? NEON : "#60a5fa",
                    }}>
                      {missed
                        ? <><PhoneMissed size={11} strokeWidth={1.9} /> Missed</>
                        : isOut
                          ? <><ArrowUpRight size={11} strokeWidth={1.9} /> Outgoing</>
                          : <><ArrowDownLeft size={11} strokeWidth={1.9} /> Incoming</>}
                    </span>
                    <span style={{ color:"rgba(255,255,255,0.15)", fontSize:10 }}>·</span>
                    <span style={{ color:"rgba(255,255,255,0.3)", fontSize:11 }}>{niceTime(log.started_at)}</span>
                  </div>
                  <p style={{ color: missed ? "#f87171" : "#fff", fontWeight:600, fontSize:13, marginBottom:1 }}>
                    {log.other_name || "Unknown"}
                  </p>
                  <p style={{ color:"rgba(255,255,255,0.3)", fontSize:11 }}>
                    {isVideo ? <Video size={12} strokeWidth={1.9} style={{ verticalAlign:"-2px" }} /> : <Phone size={12} strokeWidth={1.9} style={{ verticalAlign:"-2px" }} />} {isVideo ? "Video" : "Audio"} · {fmt(log.duration_secs)}
                  </p>
                </div>

                {/* Call back button */}
                <button
                  onClick={() => onCall?.({ id: log.other_id, full_name: log.other_name }, type === "audio")}
                  style={{
                    width:34, height:34, borderRadius:"50%", flexShrink:0,
                    background:"rgba(0,255,153,0.1)", border:"1px solid rgba(0,255,153,0.3)",
                    color:NEON, cursor:"pointer", fontSize:14,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    transition:"all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background=`${NEON}20`; e.currentTarget.style.borderColor=`${NEON}60`; }}
                  onMouseLeave={e => { e.currentTarget.style.background="rgba(0,255,153,0.1)"; e.currentTarget.style.borderColor="rgba(0,255,153,0.3)"; }}
                  title={`Call back ${log.other_name}`}
                >
                  {isVideo ? <Video size={16} strokeWidth={1.9} /> : <Phone size={16} strokeWidth={1.9} />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}